import { type RowSelectionFlattened } from '../state/types.ts';

/**
 * Merges closed row intervals into a minimal segment list.
 * Adjacent or overlapping intervals become one segment; gaps stay separate.
 * Used for row flattening and for counting selected rows without double-counting overlaps.
 */
export function mergeRowSegments(
  intervals: { minRow: number; maxRow: number }[]
): RowSelectionFlattened {
  if (intervals.length === 0) {
    return [];
  }

  const normalized = intervals.map(({ minRow, maxRow }) => ({
    minRow: Math.min(minRow, maxRow),
    maxRow: Math.max(minRow, maxRow)
  }));

  const sorted = [...normalized].sort((left, right) => left.minRow - right.minRow);
  const merged: RowSelectionFlattened = [
    { minRow: sorted[0].minRow, maxRow: sorted[0].maxRow }
  ];

  for (let sortedIndex = 1; sortedIndex < sorted.length; sortedIndex++) {
    const current = sorted[sortedIndex];
    const previousMerged = merged[merged.length - 1];

    if (current.minRow <= previousMerged.maxRow + 1) {
      previousMerged.maxRow = Math.max(previousMerged.maxRow, current.maxRow);
    } else {
      merged.push({ minRow: current.minRow, maxRow: current.maxRow });
    }
  }

  return merged;
}
